// Medical data with translations for i18n support
// Structure ready for database migration
import type { SupportedLocaleType } from "./languages";

export type TranslatedItem = {
  key: string;
  translations: {
    en: string;
    fr: string;
  };
};

export const MEDICAL_SPECIALTIES: TranslatedItem[] = [
  { key: "addictology", translations: { en: "Addictology", fr: "Addictologie" } },
  { key: "allergology", translations: { en: "Allergology", fr: "Allergologie" } },
  { key: "anesthesiology", translations: { en: "Anesthesiology-Resuscitation", fr: "Anesthésie-Réanimation" } },
  { key: "biologicalMedicine", translations: { en: "Biological Medicine", fr: "Biologie médicale" } },
  { key: "cardiacSurgery", translations: { en: "Cardiac Surgery", fr: "Chirurgie cardiaque" } },
  { key: "cardiology", translations: { en: "Cardiology", fr: "Cardiologie" } },
  { key: "cosmeticSurgery", translations: { en: "Cosmetic Surgery", fr: "Chirurgie esthétique" } },
  { key: "dermatology", translations: { en: "Dermatology", fr: "Dermatologie" } },
  { key: "diabetology", translations: { en: "Diabetology", fr: "Diabétologie" } },
  { key: "emergencyMedicine", translations: { en: "Emergency Medicine", fr: "Médecine d'urgence" } },
  { key: "endocrinology", translations: { en: "Endocrinology", fr: "Endocrinologie" } },
  { key: "ent", translations: { en: "ENT", fr: "ORL" } },
  { key: "gastroenterology", translations: { en: "Gastroenterology", fr: "Gastro-entérologie" } },
  { key: "generalMedicine", translations: { en: "General Medicine", fr: "Médecine générale" } },
  { key: "generalSurgery", translations: { en: "General Surgery", fr: "Chirurgie générale" } },
  { key: "genetics", translations: { en: "Genetics", fr: "Génétique médicale" } },
  { key: "geriatrics", translations: { en: "Geriatrics", fr: "Gériatrie" } },
  { key: "gynecology", translations: { en: "Gynecology", fr: "Gynécologie" } },
  { key: "hematology", translations: { en: "Hematology", fr: "Hématologie" } },
  { key: "hepatology", translations: { en: "Hepatology", fr: "Hépatologie" } },
  { key: "infectiousDiseases", translations: { en: "Infectious Diseases", fr: "Infectiologie" } },
  { key: "intensiveCare", translations: { en: "Intensive Care", fr: "Réanimation médicale" } },
  { key: "internalMedicine", translations: { en: "Internal Medicine", fr: "Médecine interne" } },
  { key: "maxillofacialSurgery", translations: { en: "Maxillofacial Surgery", fr: "Chirurgie maxillo-faciale" } },
  { key: "nephrology", translations: { en: "Nephrology", fr: "Néphrologie" } },
  { key: "neurology", translations: { en: "Neurology", fr: "Neurologie" } },
  { key: "neurosurgery", translations: { en: "Neurosurgery", fr: "Neurochirurgie" } },
  { key: "nuclearMedicine", translations: { en: "Nuclear Medicine", fr: "Médecine nucléaire" } },
  { key: "nutrition", translations: { en: "Nutrition", fr: "Nutrition" } },
  { key: "obstetrics", translations: { en: "Obstetrics", fr: "Obstétrique" } },
  { key: "occupationalMedicine", translations: { en: "Occupational Medicine", fr: "Médecine du travail" } },
  { key: "oncology", translations: { en: "Oncology", fr: "Oncologie" } },
  { key: "ophthalmology", translations: { en: "Ophthalmology", fr: "Ophtalmologie" } },
  { key: "orthopedics", translations: { en: "Orthopedics", fr: "Orthopédie" } },
  { key: "palliativeCare", translations: { en: "Palliative Care", fr: "Soins palliatifs" } },
  { key: "pathology", translations: { en: "Pathology", fr: "Anatomopathologie" } },
  { key: "pediatrics", translations: { en: "Pediatrics", fr: "Pédiatrie" } },
  { key: "pediatricSurgery", translations: { en: "Pediatric Surgery", fr: "Chirurgie pédiatrique" } },
  { key: "physicalMedicine", translations: { en: "Physical Medicine", fr: "Médecine physique" } },
  {
    key: "plasticSurgery",
    translations: { en: "Plastic Reconstructive Surgery", fr: "Chirurgie plastique reconstructrice" },
  },
  { key: "psychiatry", translations: { en: "Psychiatry", fr: "Psychiatrie" } },
  { key: "pulmonology", translations: { en: "Pulmonology", fr: "Pneumologie" } },
  { key: "radiology", translations: { en: "Radiology", fr: "Radiologie" } },
  { key: "rehabilitation", translations: { en: "Rehabilitation", fr: "Rééducation" } },
  { key: "rheumatology", translations: { en: "Rheumatology", fr: "Rhumatologie" } },
  { key: "sportsMedicine", translations: { en: "Sports Medicine", fr: "Médecine du sport" } },
  { key: "thoracicSurgery", translations: { en: "Thoracic Surgery", fr: "Chirurgie thoracique" } },
  { key: "transfusionMedicine", translations: { en: "Transfusion Medicine", fr: "Médecine transfusionnelle" } },
  { key: "urology", translations: { en: "Urology", fr: "Urologie" } },
  { key: "vascularSurgery", translations: { en: "Vascular Surgery", fr: "Chirurgie vasculaire" } },
];

// Extract keys for Zod validation
export const MEDICAL_SPECIALTY_KEYS = MEDICAL_SPECIALTIES.map((s) => s.key) as [string, ...string[]];
export type MedicalSpecialtyKey = (typeof MEDICAL_SPECIALTY_KEYS)[number];

export const OPERATIONS_BY_SPECIALTY: Record<string, TranslatedItem[]> = {
  cardiology: [
    { key: "coronaryBypass", translations: { en: "Coronary bypass", fr: "Pontage coronarien" } },
    { key: "angioplasty", translations: { en: "Angioplasty", fr: "Angioplastie" } },
    { key: "pacemakerImplantation", translations: { en: "Pacemaker implantation", fr: "Pose de pacemaker" } },
    { key: "valveReplacement", translations: { en: "Valve replacement", fr: "Remplacement valvulaire" } },
    { key: "cardiacAblation", translations: { en: "Cardiac ablation", fr: "Ablation cardiaque" } },
    { key: "cardiacCatheterization", translations: { en: "Cardiac catheterization", fr: "Cathétérisme cardiaque" } },
    {
      key: "implantableDefibrillator",
      translations: { en: "Implantable defibrillator", fr: "Défibrillateur implantable" },
    },
  ],
  orthopedics: [
    { key: "hipReplacement", translations: { en: "Hip replacement", fr: "Prothèse de hanche" } },
    { key: "kneeReplacement", translations: { en: "Knee replacement", fr: "Prothèse de genou" } },
    { key: "arthroscopy", translations: { en: "Arthroscopy", fr: "Arthroscopie" } },
    { key: "osteosynthesis", translations: { en: "Osteosynthesis", fr: "Ostéosynthèse" } },
    { key: "ligamentoplasty", translations: { en: "Ligamentoplasty", fr: "Ligamentoplastie" } },
    { key: "meniscectomy", translations: { en: "Meniscectomy", fr: "Méniscectomie" } },
    { key: "shoulderReplacement", translations: { en: "Shoulder replacement", fr: "Prothèse d'épaule" } },
  ],
  neurology: [
    { key: "craniotomy", translations: { en: "Craniotomy", fr: "Craniotomie" } },
    { key: "ventricularShunt", translations: { en: "Ventricular shunt", fr: "Dérivation ventriculaire" } },
    { key: "tumorResection", translations: { en: "Tumor resection", fr: "Résection tumorale" } },
    {
      key: "deepBrainStimulation",
      translations: { en: "Deep brain stimulation", fr: "Stimulation cérébrale profonde" },
    },
    { key: "embolization", translations: { en: "Embolization", fr: "Embolisation" } },
    { key: "brainBiopsy", translations: { en: "Brain biopsy", fr: "Biopsie cérébrale" } },
  ],
  gastroenterology: [
    { key: "colonoscopy", translations: { en: "Colonoscopy", fr: "Coloscopie" } },
    { key: "endoscopy", translations: { en: "Endoscopy", fr: "Endoscopie" } },
    { key: "intestinalResection", translations: { en: "Intestinal resection", fr: "Résection intestinale" } },
    { key: "cholecystectomy", translations: { en: "Cholecystectomy", fr: "Cholécystectomie" } },
    { key: "gastrectomy", translations: { en: "Gastrectomy", fr: "Gastrectomie" } },
    { key: "hepatectomy", translations: { en: "Hepatectomy", fr: "Hépatectomie" } },
  ],
  pulmonology: [
    { key: "bronchoscopy", translations: { en: "Bronchoscopy", fr: "Bronchoscopie" } },
    { key: "lobectomy", translations: { en: "Lobectomy", fr: "Lobectomie" } },
    { key: "pleuroscopy", translations: { en: "Pleuroscopy", fr: "Pleuroscopie" } },
    { key: "lungBiopsy", translations: { en: "Lung biopsy", fr: "Biopsie pulmonaire" } },
    { key: "pneumonectomy", translations: { en: "Pneumonectomy", fr: "Pneumonectomie" } },
    { key: "pleuralDrainage", translations: { en: "Pleural drainage", fr: "Drainage pleural" } },
  ],
  urology: [
    { key: "prostatectomy", translations: { en: "Prostatectomy", fr: "Prostatectomie" } },
    { key: "nephrectomy", translations: { en: "Nephrectomy", fr: "Néphrectomie" } },
    { key: "cystoscopy", translations: { en: "Cystoscopy", fr: "Cystoscopie" } },
    { key: "lithotripsy", translations: { en: "Lithotripsy", fr: "Lithotritie" } },
    { key: "kidneyTransplant", translations: { en: "Kidney transplant", fr: "Transplantation rénale" } },
    { key: "transurethralResection", translations: { en: "Transurethral resection", fr: "Résection transurétrale" } },
  ],
  gynecology: [
    { key: "hysterectomy", translations: { en: "Hysterectomy", fr: "Hystérectomie" } },
    { key: "cesarean", translations: { en: "Cesarean section", fr: "Césarienne" } },
    {
      key: "gynecologicalLaparoscopy",
      translations: { en: "Gynecological laparoscopy", fr: "Laparoscopie gynécologique" },
    },
    { key: "conization", translations: { en: "Conization", fr: "Conisation" } },
    { key: "oophorectomy", translations: { en: "Oophorectomy", fr: "Ovariectomie" } },
    { key: "myomectomy", translations: { en: "Myomectomy", fr: "Myomectomie" } },
  ],
  pediatrics: [
    { key: "circumcision", translations: { en: "Circumcision", fr: "Circoncision" } },
    { key: "pediatricAppendectomy", translations: { en: "Pediatric appendectomy", fr: "Appendicectomie pédiatrique" } },
    { key: "inguinalHerniaRepair", translations: { en: "Inguinal hernia repair", fr: "Cure de hernie inguinale" } },
    { key: "tonsillectomy", translations: { en: "Tonsillectomy", fr: "Amygdalectomie" } },
    { key: "adenoidectomy", translations: { en: "Adenoidectomy", fr: "Adénoïdectomie" } },
    {
      key: "pediatricCardiacSurgery",
      translations: { en: "Pediatric cardiac surgery", fr: "Chirurgie cardiaque pédiatrique" },
    },
  ],
  dermatology: [
    { key: "lesionExcision", translations: { en: "Lesion excision", fr: "Exérèse de lésion" } },
    { key: "skinBiopsy", translations: { en: "Skin biopsy", fr: "Biopsie cutanée" } },
    { key: "cryotherapy", translations: { en: "Cryotherapy", fr: "Cryothérapie" } },
    { key: "laserTherapy", translations: { en: "Laser therapy", fr: "Laser thérapeutique" } },
    { key: "skinGraft", translations: { en: "Skin graft", fr: "Greffe de peau" } },
    { key: "mohsSurgery", translations: { en: "Mohs surgery", fr: "Mohs chirurgie" } },
  ],
  ophthalmology: [
    { key: "cataractSurgery", translations: { en: "Cataract surgery", fr: "Chirurgie de la cataracte" } },
    { key: "vitrectomy", translations: { en: "Vitrectomy", fr: "Vitrectomie" } },
    { key: "cornealTransplant", translations: { en: "Corneal transplant", fr: "Greffe de cornée" } },
    { key: "retinalSurgery", translations: { en: "Retinal surgery", fr: "Chirurgie rétinienne" } },
    { key: "glaucomaSurgery", translations: { en: "Glaucoma surgery", fr: "Chirurgie du glaucome" } },
    { key: "refractiveSurgery", translations: { en: "Refractive surgery", fr: "Chirurgie réfractive" } },
  ],
  ent: [
    { key: "tonsillectomy", translations: { en: "Tonsillectomy", fr: "Amygdalectomie" } },
    { key: "septoplasty", translations: { en: "Septoplasty", fr: "Septoplastie" } },
    { key: "tympanoplasty", translations: { en: "Tympanoplasty", fr: "Tympanoplastie" } },
    { key: "laryngectomy", translations: { en: "Laryngectomy", fr: "Laryngectomie" } },
    { key: "rhinoplasty", translations: { en: "Rhinoplasty", fr: "Rhinoplastie" } },
    { key: "stapedectomy", translations: { en: "Stapedectomy", fr: "Stapédectomie" } },
  ],
  psychiatry: [
    {
      key: "electroconvulsiveTherapy",
      translations: { en: "Electroconvulsive therapy", fr: "Électroconvulsivothérapie" },
    },
    {
      key: "transcranialMagneticStimulation",
      translations: { en: "Transcranial magnetic stimulation", fr: "Stimulation magnétique transcrânienne" },
    },
    {
      key: "deepBrainStimulation",
      translations: { en: "Deep brain stimulation", fr: "Stimulation cérébrale profonde" },
    },
  ],
  radiology: [
    { key: "mri", translations: { en: "MRI", fr: "IRM" } },
    { key: "ctScan", translations: { en: "CT scan", fr: "Scanner" } },
    { key: "ultrasound", translations: { en: "Ultrasound", fr: "Échographie" } },
    { key: "xray", translations: { en: "X-ray", fr: "Radiographie" } },
    { key: "angiography", translations: { en: "Angiography", fr: "Angiographie" } },
    { key: "scintigraphy", translations: { en: "Scintigraphy", fr: "Scintigraphie" } },
  ],
  anesthesiology: [
    { key: "generalAnesthesia", translations: { en: "General anesthesia", fr: "Anesthésie générale" } },
    { key: "regionalAnesthesia", translations: { en: "Regional anesthesia", fr: "Anesthésie locorégionale" } },
    { key: "sedation", translations: { en: "Sedation", fr: "Sédation" } },
    { key: "postoperativeCare", translations: { en: "Postoperative care", fr: "Réanimation post-opératoire" } },
    { key: "epidural", translations: { en: "Epidural", fr: "Péridurale" } },
    { key: "spinalAnesthesia", translations: { en: "Spinal anesthesia", fr: "Rachianesthésie" } },
  ],
  generalSurgery: [
    { key: "appendectomy", translations: { en: "Appendectomy", fr: "Appendicectomie" } },
    { key: "cholecystectomy", translations: { en: "Cholecystectomy", fr: "Cholécystectomie" } },
    { key: "inguinalHernia", translations: { en: "Inguinal hernia", fr: "Hernie inguinale" } },
    { key: "thyroidectomy", translations: { en: "Thyroidectomy", fr: "Thyroïdectomie" } },
    { key: "mastectomy", translations: { en: "Mastectomy", fr: "Mastectomie" } },
    { key: "colectomy", translations: { en: "Colectomy", fr: "Colectomie" } },
  ],
  endocrinology: [
    { key: "diabetesManagement", translations: { en: "Diabetes management", fr: "Gestion du diabète" } },
    {
      key: "thyroidDisorders",
      translations: { en: "Thyroid disorders treatment", fr: "Traitement troubles thyroïdiens" },
    },
    { key: "hormonalTherapy", translations: { en: "Hormonal therapy", fr: "Hormonothérapie" } },
    {
      key: "adrenalDisorders",
      translations: { en: "Adrenal disorders treatment", fr: "Traitement troubles surrénaliens" },
    },
    {
      key: "metabolicDisorders",
      translations: { en: "Metabolic disorders management", fr: "Gestion troubles métaboliques" },
    },
  ],
  obstetrics: [
    { key: "prenatalCare", translations: { en: "Prenatal care", fr: "Suivi prénatal" } },
    { key: "cesareanSection", translations: { en: "Cesarean section", fr: "Césarienne" } },
    { key: "vaginalDelivery", translations: { en: "Vaginal delivery", fr: "Accouchement par voie basse" } },
    { key: "episiotomy", translations: { en: "Episiotomy", fr: "Épisiotomie" } },
    { key: "amniocentesis", translations: { en: "Amniocentesis", fr: "Amniocentèse" } },
    { key: "postnatalCare", translations: { en: "Postnatal care", fr: "Suivi post-partum" } },
  ],
  nephrology: [
    { key: "dialysis", translations: { en: "Dialysis", fr: "Dialyse" } },
    { key: "kidneyTransplant", translations: { en: "Kidney transplant", fr: "Transplantation rénale" } },
    { key: "renalBiopsy", translations: { en: "Renal biopsy", fr: "Biopsie rénale" } },
    {
      key: "ckdManagement",
      translations: { en: "Chronic kidney disease management", fr: "Gestion insuffisance rénale chronique" },
    },
    { key: "electrolytesManagement", translations: { en: "Electrolytes management", fr: "Gestion des électrolytes" } },
  ],
  rheumatology: [
    { key: "arthritisManagement", translations: { en: "Arthritis management", fr: "Gestion de l'arthrite" } },
    { key: "lupusTreatment", translations: { en: "Lupus treatment", fr: "Traitement du lupus" } },
    { key: "jointInjection", translations: { en: "Joint injection", fr: "Infiltration articulaire" } },
    { key: "biologicTherapy", translations: { en: "Biologic therapy", fr: "Biothérapie" } },
    { key: "osteoporosisTreatment", translations: { en: "Osteoporosis treatment", fr: "Traitement de l'ostéoporose" } },
  ],
  hematology: [
    { key: "chemotherapy", translations: { en: "Chemotherapy", fr: "Chimiothérapie" } },
    { key: "boneMarrowTransplant", translations: { en: "Bone marrow transplant", fr: "Greffe de moelle osseuse" } },
    { key: "bloodTransfusion", translations: { en: "Blood transfusion", fr: "Transfusion sanguine" } },
    { key: "anemiaManagement", translations: { en: "Anemia management", fr: "Gestion de l'anémie" } },
    {
      key: "coagulationDisorders",
      translations: { en: "Coagulation disorders treatment", fr: "Traitement troubles de la coagulation" },
    },
  ],
  infectiousDiseases: [
    { key: "antibioticTherapy", translations: { en: "Antibiotic therapy", fr: "Antibiothérapie" } },
    { key: "antiviralTherapy", translations: { en: "Antiviral therapy", fr: "Traitement antiviral" } },
    { key: "hivManagement", translations: { en: "HIV management", fr: "Gestion du VIH" } },
    {
      key: "tuberculosisTreatment",
      translations: { en: "Tuberculosis treatment", fr: "Traitement de la tuberculose" },
    },
    { key: "vaccinationProgram", translations: { en: "Vaccination program", fr: "Programme de vaccination" } },
  ],
  emergencyMedicine: [
    { key: "traumaManagement", translations: { en: "Trauma management", fr: "Gestion des traumatismes" } },
    { key: "cardiacArrest", translations: { en: "Cardiac arrest management", fr: "Gestion arrêt cardiaque" } },
    { key: "acutePoisoning", translations: { en: "Acute poisoning treatment", fr: "Traitement intoxication aiguë" } },
    { key: "emergencySurgery", translations: { en: "Emergency surgery", fr: "Chirurgie d'urgence" } },
    { key: "shockManagement", translations: { en: "Shock management", fr: "Gestion du choc" } },
  ],
  intensiveCare: [
    { key: "mechanicalVentilation", translations: { en: "Mechanical ventilation", fr: "Ventilation mécanique" } },
    { key: "sepsisTreatment", translations: { en: "Sepsis treatment", fr: "Traitement du sepsis" } },
    {
      key: "multiorganFailure",
      translations: { en: "Multi-organ failure management", fr: "Gestion défaillance multi-organes" },
    },
    { key: "hemodynamicSupport", translations: { en: "Hemodynamic support", fr: "Support hémodynamique" } },
    { key: "ecmo", translations: { en: "ECMO", fr: "ECMO" } },
  ],
  generalMedicine: [
    {
      key: "chronicDiseaseManagement",
      translations: { en: "Chronic disease management", fr: "Gestion maladies chroniques" },
    },
    { key: "preventiveCare", translations: { en: "Preventive care", fr: "Soins préventifs" } },
    { key: "healthScreening", translations: { en: "Health screening", fr: "Dépistage" } },
    { key: "vaccinationFollowUp", translations: { en: "Vaccination follow-up", fr: "Suivi vaccinal" } },
    { key: "medicationReview", translations: { en: "Medication review", fr: "Révision médicamenteuse" } },
  ],
  internalMedicine: [
    {
      key: "multimorbidityManagement",
      translations: { en: "Multimorbidity management", fr: "Gestion de la polymorbidité" },
    },
    { key: "diagnosticWorkup", translations: { en: "Diagnostic workup", fr: "Bilan diagnostique" } },
    { key: "complexCaseManagement", translations: { en: "Complex case management", fr: "Gestion de cas complexes" } },
    { key: "hospitalMedicine", translations: { en: "Hospital medicine", fr: "Médecine hospitalière" } },
  ],
  geriatrics: [
    { key: "fallPrevention", translations: { en: "Fall prevention", fr: "Prévention des chutes" } },
    { key: "dementiaManagement", translations: { en: "Dementia management", fr: "Gestion de la démence" } },
    { key: "polypharmacyReview", translations: { en: "Polypharmacy review", fr: "Révision de la polymédication" } },
    { key: "functionalAssessment", translations: { en: "Functional assessment", fr: "Évaluation fonctionnelle" } },
    { key: "elderAbusePrevention", translations: { en: "Elder abuse prevention", fr: "Prévention maltraitance" } },
  ],
  palliativeCare: [
    { key: "painManagement", translations: { en: "Pain management", fr: "Gestion de la douleur" } },
    { key: "symptomControl", translations: { en: "Symptom control", fr: "Contrôle des symptômes" } },
    { key: "endOfLifeCare", translations: { en: "End-of-life care", fr: "Soins de fin de vie" } },
    { key: "psychosocialSupport", translations: { en: "Psychosocial support", fr: "Soutien psychosocial" } },
    { key: "familyCounseling", translations: { en: "Family counseling", fr: "Accompagnement familial" } },
  ],
  physicalMedicine: [
    { key: "rehabilitationProgram", translations: { en: "Rehabilitation program", fr: "Programme de rééducation" } },
    { key: "physiotherapy", translations: { en: "Physiotherapy", fr: "Kinésithérapie" } },
    { key: "occupationalTherapy", translations: { en: "Occupational therapy", fr: "Ergothérapie" } },
    { key: "electrotherapy", translations: { en: "Electrotherapy", fr: "Électrothérapie" } },
    { key: "balneotherapy", translations: { en: "Balneotherapy", fr: "Balnéothérapie" } },
  ],
  vascularSurgery: [
    { key: "aorticAneurysm", translations: { en: "Aortic aneurysm repair", fr: "Réparation anévrisme aortique" } },
    {
      key: "carotidEndarterectomy",
      translations: { en: "Carotid endarterectomy", fr: "Endartériectomie carotidienne" },
    },
    { key: "peripheralBypass", translations: { en: "Peripheral bypass", fr: "Pontage périphérique" } },
    { key: "varicoseVeinsSurgery", translations: { en: "Varicose veins surgery", fr: "Chirurgie varices" } },
    { key: "arteriovenousFistula", translations: { en: "Arteriovenous fistula", fr: "Fistule artério-veineuse" } },
  ],
  thoracicSurgery: [
    { key: "lungResection", translations: { en: "Lung resection", fr: "Résection pulmonaire" } },
    { key: "esophagectomy", translations: { en: "Esophagectomy", fr: "Œsophagectomie" } },
    { key: "mediastinoscopy", translations: { en: "Mediastinoscopy", fr: "Médiastinoscopie" } },
    { key: "thoracotomy", translations: { en: "Thoracotomy", fr: "Thoracotomie" } },
    { key: "pleurectomy", translations: { en: "Pleurectomy", fr: "Pleurectomie" } },
  ],
  cardiacSurgery: [
    { key: "coronaryBypass", translations: { en: "Coronary bypass", fr: "Pontage coronarien" } },
    { key: "valveReplacement", translations: { en: "Valve replacement", fr: "Remplacement valvulaire" } },
    { key: "heartTransplant", translations: { en: "Heart transplant", fr: "Transplantation cardiaque" } },
    { key: "aorticSurgery", translations: { en: "Aortic surgery", fr: "Chirurgie aortique" } },
    {
      key: "congenitalHeartDefect",
      translations: { en: "Congenital heart defect repair", fr: "Réparation cardiopathie congénitale" },
    },
  ],
  neurosurgery: [
    { key: "brainTumorResection", translations: { en: "Brain tumor resection", fr: "Résection tumeur cérébrale" } },
    { key: "spinalSurgery", translations: { en: "Spinal surgery", fr: "Chirurgie rachidienne" } },
    { key: "aneurysmClipping", translations: { en: "Aneurysm clipping", fr: "Clippage d'anévrisme" } },
    { key: "discectomy", translations: { en: "Discectomy", fr: "Discectomie" } },
    { key: "ventriculostomy", translations: { en: "Ventriculostomy", fr: "Ventriculostomie" } },
  ],
  plasticSurgery: [
    { key: "breastReconstruction", translations: { en: "Breast reconstruction", fr: "Reconstruction mammaire" } },
    { key: "burnReconstruction", translations: { en: "Burn reconstruction", fr: "Reconstruction après brûlure" } },
    { key: "handSurgery", translations: { en: "Hand surgery", fr: "Chirurgie de la main" } },
    { key: "microsurgery", translations: { en: "Microsurgery", fr: "Microchirurgie" } },
    {
      key: "traumaReconstruction",
      translations: { en: "Trauma reconstruction", fr: "Reconstruction post-traumatique" },
    },
  ],
  cosmeticSurgery: [
    { key: "rhinoplasty", translations: { en: "Rhinoplasty", fr: "Rhinoplastie" } },
    { key: "facelift", translations: { en: "Facelift", fr: "Lifting facial" } },
    { key: "liposuction", translations: { en: "Liposuction", fr: "Liposuccion" } },
    { key: "breastAugmentation", translations: { en: "Breast augmentation", fr: "Augmentation mammaire" } },
    { key: "abdominoplasty", translations: { en: "Abdominoplasty", fr: "Abdominoplastie" } },
    { key: "blepharoplasty", translations: { en: "Blepharoplasty", fr: "Blépharoplastie" } },
  ],
  maxillofacialSurgery: [
    { key: "orthognathicSurgery", translations: { en: "Orthognathic surgery", fr: "Chirurgie orthognathique" } },
    { key: "facialTrauma", translations: { en: "Facial trauma repair", fr: "Réparation traumatisme facial" } },
    { key: "cleftLipPalate", translations: { en: "Cleft lip/palate repair", fr: "Réparation fente labio-palatine" } },
    { key: "tmjSurgery", translations: { en: "TMJ surgery", fr: "Chirurgie ATM" } },
    { key: "dentalImplants", translations: { en: "Dental implants", fr: "Implants dentaires" } },
  ],
  pediatricSurgery: [
    {
      key: "congenitalAnomalies",
      translations: { en: "Congenital anomalies repair", fr: "Réparation anomalies congénitales" },
    },
    { key: "pediatricAppendectomy", translations: { en: "Pediatric appendectomy", fr: "Appendicectomie pédiatrique" } },
    { key: "pyloromyotomy", translations: { en: "Pyloromyotomy", fr: "Pyloromyotomie" } },
    { key: "herniaRepair", translations: { en: "Hernia repair", fr: "Cure de hernie" } },
    { key: "neonatalSurgery", translations: { en: "Neonatal surgery", fr: "Chirurgie néonatale" } },
  ],
  hepatology: [
    { key: "liverBiopsy", translations: { en: "Liver biopsy", fr: "Biopsie hépatique" } },
    { key: "liverTransplant", translations: { en: "Liver transplant", fr: "Transplantation hépatique" } },
    { key: "cirrhosisManagement", translations: { en: "Cirrhosis management", fr: "Gestion de la cirrhose" } },
    { key: "hepatitisTreatment", translations: { en: "Hepatitis treatment", fr: "Traitement de l'hépatite" } },
    { key: "ascitesManagement", translations: { en: "Ascites management", fr: "Gestion de l'ascite" } },
  ],
  allergology: [
    { key: "allergyTesting", translations: { en: "Allergy testing", fr: "Tests allergologiques" } },
    { key: "immunotherapy", translations: { en: "Immunotherapy", fr: "Immunothérapie" } },
    { key: "anaphylaxisManagement", translations: { en: "Anaphylaxis management", fr: "Gestion de l'anaphylaxie" } },
    { key: "asthmaManagement", translations: { en: "Asthma management", fr: "Gestion de l'asthme" } },
    {
      key: "foodAllergyManagement",
      translations: { en: "Food allergy management", fr: "Gestion allergies alimentaires" },
    },
  ],
  nuclearMedicine: [
    { key: "petScan", translations: { en: "PET scan", fr: "TEP scan" } },
    { key: "thyroidScan", translations: { en: "Thyroid scan", fr: "Scintigraphie thyroïdienne" } },
    { key: "boneScan", translations: { en: "Bone scan", fr: "Scintigraphie osseuse" } },
    { key: "radioiodineTherapy", translations: { en: "Radioiodine therapy", fr: "Traitement à l'iode radioactif" } },
    {
      key: "cardiacPerfusion",
      translations: { en: "Cardiac perfusion scan", fr: "Scintigraphie de perfusion cardiaque" },
    },
  ],
  sportsMedicine: [
    {
      key: "sportsInjuryManagement",
      translations: { en: "Sports injury management", fr: "Gestion blessures sportives" },
    },
    {
      key: "performanceOptimization",
      translations: { en: "Performance optimization", fr: "Optimisation performance" },
    },
    { key: "concussionProtocol", translations: { en: "Concussion protocol", fr: "Protocole commotion cérébrale" } },
    { key: "returnToPlay", translations: { en: "Return to play assessment", fr: "Évaluation retour au jeu" } },
    { key: "preventiveScreening", translations: { en: "Preventive screening", fr: "Dépistage préventif" } },
  ],
  occupationalMedicine: [
    { key: "workplaceAssessment", translations: { en: "Workplace assessment", fr: "Évaluation du poste de travail" } },
    {
      key: "occupationalDisease",
      translations: { en: "Occupational disease management", fr: "Gestion maladies professionnelles" },
    },
    {
      key: "fitnessForWork",
      translations: { en: "Fitness for work evaluation", fr: "Évaluation aptitude au travail" },
    },
    { key: "ergonomicAdvice", translations: { en: "Ergonomic advice", fr: "Conseils ergonomiques" } },
    { key: "returnToWork", translations: { en: "Return to work program", fr: "Programme retour au travail" } },
  ],
  addictology: [
    { key: "detoxification", translations: { en: "Detoxification", fr: "Sevrage" } },
    { key: "substitutionTherapy", translations: { en: "Substitution therapy", fr: "Traitement de substitution" } },
    { key: "motivationalInterview", translations: { en: "Motivational interviewing", fr: "Entretien motivationnel" } },
    { key: "relapsePrevention", translations: { en: "Relapse prevention", fr: "Prévention de la rechute" } },
    { key: "groupTherapy", translations: { en: "Group therapy", fr: "Thérapie de groupe" } },
  ],
  diabetology: [
    { key: "insulinTherapy", translations: { en: "Insulin therapy", fr: "Insulinothérapie" } },
    { key: "glucoseMonitoring", translations: { en: "Glucose monitoring", fr: "Surveillance glycémique" } },
    { key: "diabeticFootCare", translations: { en: "Diabetic foot care", fr: "Soins du pied diabétique" } },
    { key: "nutritionalCounseling", translations: { en: "Nutritional counseling", fr: "Conseil nutritionnel" } },
    {
      key: "complicationsScreening",
      translations: { en: "Complications screening", fr: "Dépistage des complications" },
    },
  ],
  nutrition: [
    { key: "dietaryAssessment", translations: { en: "Dietary assessment", fr: "Évaluation diététique" } },
    { key: "weightManagement", translations: { en: "Weight management", fr: "Gestion du poids" } },
    { key: "enteralNutrition", translations: { en: "Enteral nutrition", fr: "Nutrition entérale" } },
    { key: "parenteralNutrition", translations: { en: "Parenteral nutrition", fr: "Nutrition parentérale" } },
    {
      key: "eatingDisorders",
      translations: { en: "Eating disorders treatment", fr: "Traitement troubles alimentaires" },
    },
  ],
  genetics: [
    { key: "geneticCounseling", translations: { en: "Genetic counseling", fr: "Conseil génétique" } },
    { key: "geneticTesting", translations: { en: "Genetic testing", fr: "Tests génétiques" } },
    { key: "prenatalDiagnosis", translations: { en: "Prenatal diagnosis", fr: "Diagnostic prénatal" } },
    { key: "familyScreening", translations: { en: "Family screening", fr: "Dépistage familial" } },
    { key: "geneTherapy", translations: { en: "Gene therapy", fr: "Thérapie génique" } },
  ],
  pathology: [
    { key: "tissueBiopsy", translations: { en: "Tissue biopsy", fr: "Biopsie tissulaire" } },
    { key: "cytology", translations: { en: "Cytology", fr: "Cytologie" } },
    { key: "autopsy", translations: { en: "Autopsy", fr: "Autopsie" } },
    { key: "immunohistochemistry", translations: { en: "Immunohistochemistry", fr: "Immunohistochimie" } },
    { key: "molecularPathology", translations: { en: "Molecular pathology", fr: "Pathologie moléculaire" } },
  ],
  biologicalMedicine: [
    { key: "bloodTesting", translations: { en: "Blood testing", fr: "Analyses sanguines" } },
    { key: "microbiologyTesting", translations: { en: "Microbiology testing", fr: "Analyses microbiologiques" } },
    { key: "biochemistry", translations: { en: "Biochemistry", fr: "Biochimie" } },
    { key: "hematologyTesting", translations: { en: "Hematology testing", fr: "Analyses hématologiques" } },
    { key: "molecularBiology", translations: { en: "Molecular biology", fr: "Biologie moléculaire" } },
  ],
  transfusionMedicine: [
    { key: "bloodDonation", translations: { en: "Blood donation", fr: "Don de sang" } },
    { key: "bloodTyping", translations: { en: "Blood typing", fr: "Groupage sanguin" } },
    { key: "plateletTransfusion", translations: { en: "Platelet transfusion", fr: "Transfusion plaquettaire" } },
    { key: "plasmaTransfusion", translations: { en: "Plasma transfusion", fr: "Transfusion de plasma" } },
    { key: "apheresis", translations: { en: "Apheresis", fr: "Aphérèse" } },
  ],
  oncology: [
    { key: "chemotherapy", translations: { en: "Chemotherapy", fr: "Chimiothérapie" } },
    { key: "radiotherapy", translations: { en: "Radiotherapy", fr: "Radiothérapie" } },
    { key: "immunotherapy", translations: { en: "Immunotherapy", fr: "Immunothérapie" } },
    { key: "targetedTherapy", translations: { en: "Targeted therapy", fr: "Thérapie ciblée" } },
    { key: "tumorResection", translations: { en: "Tumor resection", fr: "Résection tumorale" } },
    { key: "boneMarrowTransplant", translations: { en: "Bone marrow transplant", fr: "Greffe de moelle osseuse" } },
    { key: "palliativeCare", translations: { en: "Palliative care", fr: "Soins palliatifs" } },
  ],
  rehabilitation: [
    { key: "physiotherapy", translations: { en: "Physiotherapy", fr: "Kinésithérapie" } },
    { key: "occupationalTherapy", translations: { en: "Occupational therapy", fr: "Ergothérapie" } },
    { key: "speechTherapy", translations: { en: "Speech therapy", fr: "Orthophonie" } },
    { key: "cardiacRehabilitation", translations: { en: "Cardiac rehabilitation", fr: "Réadaptation cardiaque" } },
    {
      key: "neurologicalRehabilitation",
      translations: { en: "Neurological rehabilitation", fr: "Rééducation neurologique" },
    },
    {
      key: "orthopedicRehabilitation",
      translations: { en: "Orthopedic rehabilitation", fr: "Rééducation orthopédique" },
    },
    {
      key: "respiratoryRehabilitation",
      translations: { en: "Respiratory rehabilitation", fr: "Réadaptation respiratoire" },
    },
  ],
};

export const MEDICAL_PROBLEM_GROUPS: TranslatedItem[] = [
  { key: "cardiovascular", translations: { en: "Cardiovascular", fr: "Cardiovasculaire" } },
  { key: "metabolic", translations: { en: "Metabolic", fr: "Métabolique" } },
  { key: "musculoskeletal", translations: { en: "Musculoskeletal", fr: "Musculo-squelettique" } },
  { key: "respiratory", translations: { en: "Respiratory", fr: "Respiratoire" } },
  { key: "renal", translations: { en: "Renal", fr: "Rénal" } },
  { key: "neurological", translations: { en: "Neurological", fr: "Neurologique" } },
  { key: "digestive", translations: { en: "Digestive", fr: "Digestif" } },
  { key: "cancer", translations: { en: "Cancer", fr: "Cancer" } },
  { key: "autoimmune", translations: { en: "Autoimmune", fr: "Auto-immun" } },
  { key: "infectious", translations: { en: "Infectious", fr: "Infectieux" } },
  { key: "allergiesDrug", translations: { en: "Drug allergies", fr: "Allergies médicamenteuses" } },
  { key: "allergiesEnvironmental", translations: { en: "Environmental allergies", fr: "Allergies environnementales" } },
  { key: "allergiesFood", translations: { en: "Food allergies", fr: "Allergies alimentaires" } },
  { key: "allergiesOther", translations: { en: "Other allergies", fr: "Autres allergies" } },
  { key: "immunodeficiency", translations: { en: "Immunodeficiency", fr: "Immunodéficience" } },
  { key: "painMentalHealth", translations: { en: "Pain & Mental health", fr: "Douleur & Santé mentale" } },
];

export interface GroupedMedicalProblem extends TranslatedItem {
  group: string;
}

export const MEDICAL_PROBLEMS: GroupedMedicalProblem[] = [
  // Cardiovascular
  { key: "heartFailure", group: "cardiovascular", translations: { en: "Heart failure", fr: "Insuffisance cardiaque" } },
  { key: "arrhythmia", group: "cardiovascular", translations: { en: "Arrhythmia", fr: "Arythmie" } },
  { key: "hypertension", group: "cardiovascular", translations: { en: "Hypertension", fr: "Hypertension" } },
  {
    key: "coronaryArteryDisease",
    group: "cardiovascular",
    translations: { en: "Coronary artery disease", fr: "Maladie coronarienne" },
  },
  { key: "stroke", group: "cardiovascular", translations: { en: "Stroke", fr: "AVC" } },

  // Metabolic
  { key: "diabetes", group: "metabolic", translations: { en: "Diabetes", fr: "Diabète" } },
  { key: "diabetesType1", group: "metabolic", translations: { en: "Type 1 diabetes", fr: "Diabète de type 1" } },
  { key: "diabetesType2", group: "metabolic", translations: { en: "Type 2 diabetes", fr: "Diabète de type 2" } },
  { key: "obesity", group: "metabolic", translations: { en: "Obesity", fr: "Obésité" } },
  {
    key: "hypercholesterolemia",
    group: "metabolic",
    translations: { en: "Hypercholesterolemia", fr: "Hypercholestérolémie" },
  },
  { key: "thyroidDisorder", group: "metabolic", translations: { en: "Thyroid disorder", fr: "Trouble thyroïdien" } },

  // Musculoskeletal
  { key: "osteoarthritis", group: "musculoskeletal", translations: { en: "Osteoarthritis", fr: "Arthrose" } },
  {
    key: "rheumatoidArthritis",
    group: "musculoskeletal",
    translations: { en: "Rheumatoid arthritis", fr: "Polyarthrite rhumatoïde" },
  },
  { key: "osteoporosis", group: "musculoskeletal", translations: { en: "Osteoporosis", fr: "Ostéoporose" } },
  { key: "fracture", group: "musculoskeletal", translations: { en: "Fracture", fr: "Fracture" } },
  { key: "backPain", group: "musculoskeletal", translations: { en: "Chronic back pain", fr: "Lombalgie chronique" } },

  // Respiratory
  { key: "asthma", group: "respiratory", translations: { en: "Asthma", fr: "Asthme" } },
  { key: "copd", group: "respiratory", translations: { en: "COPD", fr: "BPCO" } },
  { key: "sleepApnea", group: "respiratory", translations: { en: "Sleep apnea", fr: "Apnée du sommeil" } },

  // Renal
  {
    key: "chronicKidneyDisease",
    group: "renal",
    translations: { en: "Chronic kidney disease", fr: "Insuffisance rénale chronique" },
  },

  // Neurological
  { key: "parkinson", group: "neurological", translations: { en: "Parkinson's disease", fr: "Maladie de Parkinson" } },
  { key: "alzheimer", group: "neurological", translations: { en: "Alzheimer's disease", fr: "Maladie d'Alzheimer" } },
  {
    key: "multiplesclerosis",
    group: "neurological",
    translations: { en: "Multiple sclerosis", fr: "Sclérose en plaques" },
  },
  { key: "epilepsy", group: "neurological", translations: { en: "Epilepsy", fr: "Épilepsie" } },

  // Digestive
  { key: "crohnsDisease", group: "digestive", translations: { en: "Crohn's disease", fr: "Maladie de Crohn" } },
  {
    key: "ulcerativeColitis",
    group: "digestive",
    translations: { en: "Ulcerative colitis", fr: "Rectocolite hémorragique" },
  },
  { key: "hepatitis", group: "digestive", translations: { en: "Hepatitis", fr: "Hépatite" } },
  { key: "cirrhosis", group: "digestive", translations: { en: "Cirrhosis", fr: "Cirrhose" } },

  // Cancer
  { key: "cancer", group: "cancer", translations: { en: "Cancer", fr: "Cancer" } },

  // Autoimmune
  { key: "lupus", group: "autoimmune", translations: { en: "Lupus", fr: "Lupus" } },
  { key: "psoriasis", group: "autoimmune", translations: { en: "Psoriasis", fr: "Psoriasis" } },

  // Infectious
  { key: "hiv", group: "infectious", translations: { en: "HIV/AIDS", fr: "VIH/SIDA" } },
  { key: "infection", group: "infectious", translations: { en: "Infection", fr: "Infection" } },

  // Allergies - Drug
  { key: "drugAllergy", group: "allergiesDrug", translations: { en: "Drug allergy", fr: "Allergie médicamenteuse" } },
  {
    key: "penicillinAllergy",
    group: "allergiesDrug",
    translations: { en: "Penicillin allergy", fr: "Allergie à la pénicilline" },
  },
  {
    key: "nsaidAllergy",
    group: "allergiesDrug",
    translations: { en: "NSAID allergy", fr: "Allergie aux AINS (Anti-Inflammatoires Non Stéroïdiens)" },
  },
  {
    key: "contrastDyeAllergy",
    group: "allergiesDrug",
    translations: { en: "Contrast dye allergy", fr: "Allergie aux produits de contraste" },
  },
  {
    key: "anesthesiaAllergy",
    group: "allergiesDrug",
    translations: { en: "Anesthesia allergy", fr: "Allergie aux anesthésiques" },
  },

  // Allergies - Environmental (ORL)
  {
    key: "pollenAllergy",
    group: "allergiesEnvironmental",
    translations: { en: "Pollen allergy (hay fever)", fr: "Allergie au pollen (rhume des foins)" },
  },
  {
    key: "dustMiteAllergy",
    group: "allergiesEnvironmental",
    translations: { en: "Dust mite allergy", fr: "Allergie aux acariens" },
  },
  {
    key: "catAllergy",
    group: "allergiesEnvironmental",
    translations: { en: "Cat allergy", fr: "Allergie aux poils de chat" },
  },
  {
    key: "dogAllergy",
    group: "allergiesEnvironmental",
    translations: { en: "Dog allergy", fr: "Allergie aux poils de chien" },
  },
  {
    key: "moldAllergy",
    group: "allergiesEnvironmental",
    translations: { en: "Mold allergy", fr: "Allergie aux moisissures" },
  },

  // Allergies - Food
  { key: "foodAllergy", group: "allergiesFood", translations: { en: "Food allergy", fr: "Allergie alimentaire" } },
  {
    key: "peanutAllergy",
    group: "allergiesFood",
    translations: { en: "Peanut allergy", fr: "Allergie aux arachides" },
  },
  {
    key: "treeNutAllergy",
    group: "allergiesFood",
    translations: { en: "Tree nut allergy", fr: "Allergie aux fruits à coque" },
  },
  {
    key: "shellfishAllergy",
    group: "allergiesFood",
    translations: { en: "Shellfish allergy", fr: "Allergie aux fruits de mer" },
  },
  { key: "fishAllergy", group: "allergiesFood", translations: { en: "Fish allergy", fr: "Allergie au poisson" } },
  { key: "eggAllergy", group: "allergiesFood", translations: { en: "Egg allergy", fr: "Allergie aux œufs" } },
  {
    key: "milkAllergy",
    group: "allergiesFood",
    translations: { en: "Milk/Dairy allergy", fr: "Allergie au lait/produits laitiers" },
  },
  {
    key: "glutenIntolerance",
    group: "allergiesFood",
    translations: { en: "Gluten intolerance/Celiac", fr: "Intolérance au gluten/Maladie cœliaque" },
  },

  // Allergies - Other
  { key: "latexAllergy", group: "allergiesOther", translations: { en: "Latex allergy", fr: "Allergie au latex" } },
  {
    key: "insectStingAllergy",
    group: "allergiesOther",
    translations: { en: "Insect sting allergy", fr: "Allergie aux piqûres d'insectes" },
  },

  // Immunodeficiency
  {
    key: "immunodeficiency",
    group: "immunodeficiency",
    translations: { en: "Immunodeficiency", fr: "Immunodéficience" },
  },

  // Pain & Mental health
  { key: "chronicPain", group: "painMentalHealth", translations: { en: "Chronic pain", fr: "Douleur chronique" } },
  { key: "fibromyalgia", group: "painMentalHealth", translations: { en: "Fibromyalgia", fr: "Fibromyalgie" } },
  {
    key: "sleepDisorders",
    group: "painMentalHealth",
    translations: { en: "Sleep disorders", fr: "Troubles du sommeil" },
  },
  { key: "anxiety", group: "painMentalHealth", translations: { en: "Anxiety", fr: "Anxiété" } },
  { key: "depression", group: "painMentalHealth", translations: { en: "Depression", fr: "Dépression" } },
];

export const PATIENT_TYPES: TranslatedItem[] = [
  // General
  { key: "allPatients", translations: { en: "All patients", fr: "Tous patients" } },

  // Age groups
  { key: "infant", translations: { en: "Infant (0-1 year)", fr: "Nourrisson (0-1 an)" } },
  { key: "toddler", translations: { en: "Toddler (1-3 years)", fr: "Tout-petit (1-3 ans)" } },
  { key: "child", translations: { en: "Child (3-12 years)", fr: "Enfant (3-12 ans)" } },
  { key: "adolescent", translations: { en: "Adolescent (12-18 years)", fr: "Adolescent (12-18 ans)" } },
  { key: "adult", translations: { en: "Adult (18-65 years)", fr: "Adulte (18-65 ans)" } },
  { key: "seniorAdult", translations: { en: "Senior (65-75 years)", fr: "Senior (65-75 ans)" } },
  { key: "elderly", translations: { en: "Elderly (>75 years)", fr: "Personne âgée (>75 ans)" } },

  // Special conditions (non-pathological)
  { key: "pregnantWoman", translations: { en: "Pregnant woman", fr: "Femme enceinte" } },
  { key: "breastfeedingWoman", translations: { en: "Breastfeeding woman", fr: "Femme allaitante" } },
  { key: "postpartumWoman", translations: { en: "Postpartum woman", fr: "Femme en post-partum" } },
];

// Extract keys for Zod validation
export const PATIENT_TYPE_KEYS = PATIENT_TYPES.map((p) => p.key) as [string, ...string[]];
export type PatientTypeKey = (typeof PATIENT_TYPE_KEYS)[number];

export const USAGE_CONTEXTS: TranslatedItem[] = [
  { key: "postSurgery", translations: { en: "Post-surgery", fr: "Post-chirurgie" } },
  { key: "preSurgery", translations: { en: "Pre-surgery", fr: "Pré-chirurgie" } },
  { key: "preConsultation", translations: { en: "Pre-consultation", fr: "Pré-consultation" } },
  { key: "perTreatment", translations: { en: "Per-treatment", fr: "Per-traitement" } },
  { key: "medicationFollowUp", translations: { en: "Medication follow-up", fr: "Suivi médicamenteux" } },
  { key: "rehabilitation", translations: { en: "Rehabilitation", fr: "Rééducation" } },
  { key: "prevention", translations: { en: "Prevention", fr: "Prévention" } },
  { key: "monitoring", translations: { en: "Monitoring", fr: "Surveillance" } },
  { key: "other", translations: { en: "Other", fr: "Autre" } },
];

// Extract keys for Zod validation
export const USAGE_CONTEXT_KEYS = USAGE_CONTEXTS.map((c) => c.key) as [string, ...string[]];
export type UsageContextKey = (typeof USAGE_CONTEXT_KEYS)[number];

export const DESTINATIONS: TranslatedItem[] = [
  { key: "publicHospital", translations: { en: "Public hospital", fr: "Hôpital public" } },
  { key: "privateClinic", translations: { en: "Private clinic", fr: "Clinique privée" } },
  { key: "healthCenter", translations: { en: "Health center", fr: "Centre de santé" } },
  { key: "medicalOffice", translations: { en: "Medical office", fr: "Cabinet médical" } },
  {
    key: "cosmeticSurgeryCenter",
    translations: { en: "Cosmetic surgery center", fr: "Centre de chirurgie esthétique" },
  },
  { key: "rehabilitationCenter", translations: { en: "Rehabilitation center", fr: "Centre de rééducation" } },
  { key: "nursingHome", translations: { en: "Nursing home", fr: "Maison de retraite" } },
];

// Helper function to get translated value based on current locale
export function getTranslation(item: TranslatedItem, locale: SupportedLocaleType): string {
  // Type guard: seulement en et fr ont des traductions directes
  if (locale === "en" || locale === "fr") {
    return item.translations[locale];
  }
  // Fallback pour les autres langues
  return item.translations["en"] || item.translations["fr"] || item.key;
}

// Helper function to get all translated values for an array
export function getTranslatedArray(items: TranslatedItem[], locale: SupportedLocaleType): string[] {
  return items.map((item) => getTranslation(item, locale));
}
